'use client';

import { useTheme } from 'next-themes';
import { type ComponentType, type FC, useCallback, useEffect, useMemo, useState } from 'react';

import type { TWithThemeToggle } from '@/lib/interfaces/hoc/withThemeToggle.types';

const withThemeToggle = <P extends object>(WrappedComponent: ComponentType<P & TWithThemeToggle>) => {
  const WithThemeToggle: FC<P> = (props) => {
    const { setTheme, resolvedTheme } = useTheme();
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
      setMounted(true);
    }, []);

    const toggleTheme = useCallback(() => {
      // When user manually toggles, set explicit theme (not system)
      setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
    }, [resolvedTheme, setTheme]);

    const isDark = resolvedTheme === 'dark';

    const themeProps: TWithThemeToggle = useMemo(
      () => ({
        isDark: mounted ? isDark : false, // Default to light theme during SSR
        toggleTheme,
      }),
      [isDark, toggleTheme, mounted],
    );

    // Prevent hydration mismatch by not rendering until mounted
    if (!mounted) {
      return <WrappedComponent {...props} {...{ isDark: false, toggleTheme }} />;
    }

    return <WrappedComponent {...props} {...themeProps} />;
  };

  WithThemeToggle.displayName = `withThemeToggle(${WrappedComponent.displayName || WrappedComponent.name})`;

  return WithThemeToggle;
};

export default withThemeToggle;
